// Card pre-registration ("confirm your card") — customers whose subscription was
// bought without auto-renewal are asked to confirm their card once, so the next
// period is charged automatically.
//
// Pure, framework-free module: the phase derivation is unit-tested straight from
// e2e/tests/core/card-preauth-phase.spec.ts, so keep Angular and path aliases out.

export interface ICardPreauthEligibility {
    showBanner: boolean;
    eligible: boolean;
    verificationAmount: number;
    /** Null when the BE does not send an expiry — the notice then stays at its mildest. */
    expiresAt: Date | null;
    /** Card pre-authorized, not charged yet — the subscriptions page shows the confirmed state. */
    cardPreregistered: boolean;
    /** Campaign offer for this customer; null when none applies. */
    campaignBenefit: ICampaignBenefit | null;
}

/**
 * Which campaign offer a plan carries (BE `benefitType`). Two separate campaigns, not variants of
 * one: a plan carries at most one of them, and their copy is not interchangeable.
 */
export type CampaignBenefitType = 'FREEDAYS' | 'BONUSDAYS';

/**
 * Free days first, charge after: the card is verified now and first charged when the free window
 * ends. The three dates are the BE's projection — always sent with this offer; the client never
 * derives a date.
 */
export interface IFreeDaysPlanBenefit {
    type: 'FREEDAYS';
    planReferenceId: string;
    /** The free days. */
    days: number;
    freeFrom: Date;
    freeTo: Date;
    firstChargeAt: Date;
}

/**
 * Charge first, free days after: the period is paid and `days` free days follow it. When it is
 * paid follows from the customer's state, not from the offer (the BE's `bonusChargeMode` is not
 * read): on the expiry day of a live subscription, now without one. Dateless by design — the copy
 * is "pay, then get N days free".
 */
export interface IBonusDaysPlanBenefit {
    type: 'BONUSDAYS';
    planReferenceId: string;
    /** The free days added after the paid period. */
    days: number;
}

export type ICampaignPlanBenefit = IFreeDaysPlanBenefit | IBonusDaysPlanBenefit;

export interface ICampaignBenefit {
    /** The offer surfaces without a plan (header pill, toast) advertise: the one with the most days. */
    headline: ICampaignPlanBenefit;
    /** One entry per plan a campaign covers for this customer; never empty. */
    plans: ICampaignPlanBenefit[];
}

/**
 * What the payment-return page needs to word a promo activation, kept in a session marker across
 * the CorvusPay round trip: the free-days window, or the bonus offer with the renewal charge date
 * (`chargeAt`, the live subscription's expiry; null on a purchase charged now).
 */
export type IPendingPromo =
    | { type: 'FREEDAYS'; freeTo: Date; firstChargeAt: Date }
    | { type: 'BONUSDAYS'; days: number; chargeAt: Date | null };

/**
 * The subscription notice, by calendar days left (Europe/Zagreb) until expiry:
 * - `none`     — no card confirmation needed
 * - `confirm`  — more than BANNER_DAYS: header shows the expiry date + amber profile dot
 *                (compact "Potvrdi karticu" button only when the BE sends no expiry)
 * - `expiring` — 2…BANNER_DAYS: header shows the expiry date
 * - `tomorrow` — the last day(s) (1, and 0 = expires later today): amber, toast once per session
 * - `expired`  — the period is over: red, toast on every app entry, playback leads to confirmation
 */
export type CardPreauthPhase = 'none' | 'confirm' | 'expiring' | 'tomorrow' | 'expired';

/** Days left from which the header shows the expiry date. Hardcoded for now (story). */
export const BANNER_DAYS = 7;

/** Calendar days are counted in the operator's zone, not the browser's or a 24h window. */
export const NOTICE_TIME_ZONE = 'Europe/Zagreb';

const DAY_MS = 24 * 60 * 60 * 1000;

const zagrebDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: NOTICE_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
});

/** `yyyy-mm-dd` of `time` on the Zagreb calendar. */
export function zagrebDateKey(time: number): string {
    return zagrebDate.format(new Date(time));
}

/** Zagreb calendar day of `time` as a day count, so differences are whole days. */
function zagrebDay(time: number): number {
    const [y, m, d] = zagrebDateKey(time).split('-').map(Number);
    return Date.UTC(y, m - 1, d) / DAY_MS;
}

/** Whole Zagreb calendar days from `now` to `expiresAt` (0 = same day, negative = past day). */
export function calendarDaysLeft(expiresAt: Date, now: number): number {
    return zagrebDay(expiresAt.getTime()) - zagrebDay(now);
}

export function cardPreauthPhase(needsConfirmation: boolean, expiresAt: Date | null, now: number): CardPreauthPhase {
    if (!needsConfirmation) return 'none';
    if (!expiresAt) return 'confirm';
    if (now >= expiresAt.getTime()) return 'expired';
    const daysLeft = calendarDaysLeft(expiresAt, now);
    if (daysLeft <= 1) return 'tomorrow';
    if (daysLeft <= BANNER_DAYS) return 'expiring';
    return 'confirm';
}

/** "29.10." — day.month. on the Zagreb calendar, no year. */
export function noticeShortDate(date: Date): string {
    const [, m, d] = zagrebDateKey(date.getTime()).split('-').map(Number);
    return `${d}.${m}.`;
}

/** "29.10.2027." — day.month.year. on the Zagreb calendar; the end of a free period, which on a
 *  yearly plan can be a year or more away. */
export function noticeDateWithYear(date: Date): string {
    const [y, m, d] = zagrebDateKey(date.getTime()).split('-').map(Number);
    return `${d}.${m}.${y}.`;
}

/** "07.11.2026." — dd.MM.yyyy. on the Zagreb calendar, used in promo copy. */
export function promoLongDate(date: Date): string {
    const [y, m, d] = zagrebDateKey(date.getTime()).split('-').map(Number);
    return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}.`;
}
